import React, { useMemo } from 'react';
import { Column } from 'react-table';
import { Badge } from '@/components/ui/badge';

import FormattedDate from '../../../../../components/date/FormattedDate';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { TableContent } from '../../components/TableContent';
import { needsMyAction } from '../logic';
import { CompensationReview, ReviewStatus } from '../types';
import { CompensationReviewStatusBadge } from './CompensationReviewStatusBadge';

type Props = {
  reviews: CompensationReview[];
  filter: string;
  pageSize: number;
  onRowClick: (id: string) => void;
};

type CompensationReviewRow = {
  id: string;
  name: string;
  role: string;
  effectiveDate: string;
  assessingManager: string;
  status: ReviewStatus;
  needsAction: boolean;
};

export const CompensationReviewsTable: React.FC<
  React.PropsWithChildren<Props>
> = ({ reviews, filter, pageSize, onRowClick }) => {
  const t = useNamespacedTranslation('compensationReviews');

  const columns: Column<CompensationReviewRow>[] = useMemo(
    () => [
      {
        Header: t('list.name'),
        accessor: 'name',
        Cell: (props) => (
          <span className="fw-bold">
            {props.value}
            {props.row.original.needsAction && (
              <Badge variant="primary" className="ms-2">
                {t('list.actionRequired')}
              </Badge>
            )}
          </span>
        ),
      },
      {
        Header: t('list.role'),
        accessor: 'role',
      },
      {
        Header: t('list.effectiveDate'),
        accessor: 'effectiveDate',
        Cell: (props) =>
          props.value ? (
            <FormattedDate day={props.value} format="short" />
          ) : (
            <></>
          ),
      },
      {
        Header: t('list.assessingManager'),
        accessor: 'assessingManager',
      },
      {
        Header: t('list.status'),
        accessor: 'status',
        Cell: (props) => <CompensationReviewStatusBadge status={props.value} />,
      },
    ],
    [t],
  );

  const data: CompensationReviewRow[] = useMemo(
    () =>
      reviews.map((review) => ({
        id: review.id,
        name: review.name,
        role:
          review.myRole === 'APPROVER'
            ? t('roles.approver')
            : review.myRole === 'VIEWER'
            ? t('roles.viewer')
            : t('roles.reviewer'),
        effectiveDate: review.effectiveDate,
        assessingManager: review.assessingManager.name,
        status: review.status,
        needsAction: needsMyAction(review),
      })),
    [reviews, t],
  );

  return (
    <TableContent
      columns={columns}
      data={data}
      filter={filter}
      pageSize={pageSize}
      initialSortBy={[{ id: 'name', desc: false }]}
      onRowClick={(row) => onRowClick(row.id)}
      getRowProps={() => ({ className: 'compensation-review-row' })}
    />
  );
};
